import { useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { BookLink } from '@todo/web-ui/components/book-link';
import { DetailLayout } from '@todo/web-ui/components/detail-layout';
import { NotFoundScreen } from '@todo/web-ui/components/not-found-screen';
import { Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { TourFacts } from '@todo/web-ui/components/tour-facts';
import { formatDuration, formatPrice, formatRating, localePath, partnerName } from '@todo/web-ui/lib/format';
import { useTour } from '@todo/core/hooks/use-tour';
import {
  localizedTourDescription,
  localizedTourSeoTitle,
  localizedTourSummary,
  localizedTourTitle,
} from '@todo/core/i18n/tour-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import { breadcrumbJsonLd, seoTitle, toursCrumb, truncate } from '@todo/core/seo';
import { getPrefetched, prefetchElementId, serializePrefetch } from '@todo/core/static-prefetch';
import { isTourDelisted, sizedTourImage, type Tour } from '@todo/core/tours';

// One page per tour (delisted ones too, for their 30 days — see
// Tour.delisted_at), prerendered from @todo/core's build-time fetch.
export { generateTourStaticParams as generateStaticParams } from '@todo/core/detail/tour-detail';

export default function TourPage() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { tour: fetched, loading } = useTour(id);
  // The build render (and the client's first render, from the JSON baked
  // below) use the prefetched row; the client fetch then replaces it.
  const tour = fetched ?? getPrefetched<Tour>('tour', id) ?? null;

  if (!tour) return loading ? <Page>{null}</Page> : <NotFoundScreen />;
  return <TourScreen tour={tour} />;
}

function TourScreen({ tour }: { tour: Tour }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const title = localizedTourTitle(tour, locale);
  const description = localizedTourDescription(tour, locale);
  const delisted = isTourDelisted(tour);
  const image = sizedTourImage(tour.image_url, 1200, 800);
  const partner = partnerName(tour.source);

  const facts: { label: string; value: string }[] = [];
  if (tour.duration) facts.push({ label: t('detail.duration'), value: formatDuration(tour.duration, locale) });
  if (tour.rating != null && tour.review_count) {
    facts.push({
      label: t('detail.rating'),
      value: `★ ${formatRating(tour.rating, locale)} · ${t('card.reviews', { count: tour.review_count })}`,
    });
  }
  if (tour.from_price != null) {
    facts.push({
      label: t('detail.price'),
      value: t('card.from', { price: formatPrice(Number(tour.from_price), tour.currency, locale) }),
    });
  }

  return (
    <Page>
      <PageHead
        title={seoTitle(localizedTourSeoTitle(tour, locale))}
        description={localizedTourSummary(tour, locale) ?? truncate(description ?? title)}
        path={`tours/${tour.id}`}
        image={image}
        indexable={!delisted}>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [toursCrumb(locale), { name: title }]))}
        </script>
        <script id={prefetchElementId('tour', tour.id)} type="application/json">
          {serializePrefetch(tour)}
        </script>
      </PageHead>

      <DetailLayout
        crumbs={[
          { label: t('nav.home'), href: localePath(locale) },
          { label: t('nav.tours'), href: localePath(locale, 'tours') },
        ]}
        imageUrl={image}
        imageAlt={title}
        category={tour.source === 'click_and_boat' ? t('card.boat') : t('card.tour')}
        title={title}
        facts={facts}
        action={
          delisted || !tour.booking_url ? (
            <p>{t('detail.delisted')}</p>
          ) : (
            <>
              <BookLink
                url={tour.booking_url}
                label={t('detail.book')}
                meta={{ kind: 'tour', source: tour.source, id: tour.id, title, city: tour.city_id, locale }}
              />
              {partner ? <small>{t('detail.bookOn', { partner })}</small> : null}
            </>
          )
        }>
        {(description ?? '')
          .split(/\n{2,}/)
          .filter(Boolean)
          .map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        {delisted ? null : <TourFacts tour={tour} locale={locale} />}
      </DetailLayout>
    </Page>
  );
}
